import { slices } from "@/constants/sliceNames";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../../axios/defaultAxios";
import { errorMessage, successMessage } from "../../actions/messages.actions";
import { getParamsFromURL, serializeQueryParams } from "@/utils/utils";
import { DEFAULT_DATE_RANGE, getStats, processExecutions } from "./utils";

const ENDPOINT = "organization";

/******** List Organizations  **********/

export const requestOrganizations = createAsyncThunk(
  `${slices.organizations}/requestOrganizations`,
  async (filter, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${ENDPOINT}?${serializeQueryParams(filter)}`);
      const { data: organizations, current_page, page_limit, total_pages, records_count } = response.data.data;
      return {
        organizations,
        current_page,
        page_limit,
        total_pages,
        records_count,
      };
    } catch (error) {
      console.log(error.response);
      dispatch(errorMessage(error.response.data.message || error.response.data));
      return rejectWithValue(error);
    }
  }
);

export const requestOrganizationsStatus = {
  [requestOrganizations.fulfilled]: (state, action) => {
    state.items = [...action.payload.organizations];
    state.pageLimit = action.payload.page_limit;
    state.currentPage = action.payload.current_page;
    state.totalPages = action.payload.total_pages;
    state.recordsCount = action.payload.records_count;
    state.loading = false;
  },
  [requestOrganizations.pending]: (state, action) => {
    state.loading = true;
  },
  [requestOrganizations.rejected]: (state, action) => {
    state.error = action.payload.error;
    state.loading = false;
  },
};

/******** Delete Organization  **********/

export const requestDeleteOrganization = createAsyncThunk(
  `${slices.organizations}/requestDeleteOrganization`,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.delete(`${ENDPOINT}/${id}`);
      const { data } = response;
      dispatch(successMessage(data.message));
      return { ...data, id };
    } catch (error) {
      dispatch(errorMessage("Error while deleting organization"));
      return rejectWithValue({ ...error });
    }
  }
);

export const requestDeleteOrganizationStatus = {
  [requestDeleteOrganization.fulfilled]: (state, action) => {
    state.items = state.items.filter((item) => item._id !== action.payload.id);
    state.loading = false;
  },
  [requestDeleteOrganization.pending]: (state, action) => {
    state.loading = true;
  },
  [requestDeleteOrganization.rejected]: (state, action) => {
    state.error = action.payload.error;
    state.loading = false;
  },
};

/******** Create Organization  **********/

export const requestCreateOrganization = createAsyncThunk(
  `${slices.organizations}/requestCreateOrganization`,
  async ({ name, superAdminEmail, website, roles }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(ENDPOINT, {
        name,
        super_admin_email: superAdminEmail,
        website,
        roles,
      });
      const { data } = response;
      dispatch(successMessage(data.message));
      return data;
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error.response.data.error);
    }
  }
);

export const requestCreateOrganizationStatus = {
  [requestCreateOrganization.fulfilled]: (state, action) => {
    state.loading = false;
  },
  [requestCreateOrganization.pending]: (state, action) => {
    state.loading = true;
  },
  [requestCreateOrganization.rejected]: (state, action) => {
    state.error = action.payload.error;
    state.loading = false;
  },
};

/******** Get Single Organization  **********/

export const requestSingleOrganization = createAsyncThunk(
  `${slices.organizations}/requestSingleOrganization`,
  async (id, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.get(`${ENDPOINT}/${id}`);
      const { data } = response;
      return data;
    } catch (error) {
      dispatch(errorMessage(error.response.data.error));
      return rejectWithValue(error);
    }
  }
);

export const requestSingleOrganizationStatus = {
  [requestSingleOrganization.fulfilled]: (state, action) => {
    state.organization = action.payload.data;
    state.loading = false;
  },
  [requestSingleOrganization.pending]: (state, action) => {
    state.loading = true;
  },
  [requestSingleOrganization.rejected]: (state, action) => {
    state.error = action.payload.error;
    state.loading = false;
  },
};

/******** Edit Organization  **********/

export const requestEditOrganization = createAsyncThunk(
  `${slices.organizations}/requestEditOrganization`,
  async ({ id, data }, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.put(`${ENDPOINT}/${id}`, data);
      dispatch(successMessage(response.message));
      return response.data;
    } catch (error) {
      dispatch(errorMessage(error.response.message || error.response.data));
      return rejectWithValue(error);
    }
  }
);

export const requestEditOrganizationStatus = {
  [requestEditOrganization.fulfilled]: (state, action) => {
    state.organization = action.payload.data;
    state.loading = false;
  },
  [requestEditOrganization.pending]: (state, action) => {
    state.loading = true;
  },
  [requestEditOrganization.rejected]: (state, action) => {
    state.error = action.payload.error;
    state.loading = false;
  },
};

/******** Get Organization States  **********/

export const requestOrganizationsStats = createAsyncThunk(
  `${slices.organizations}/requestOrganizationsStats`,
  async ({ type = "All", dateRange = DEFAULT_DATE_RANGE }, { rejectWithValue, dispatch }) => {
    try {
      const params = getParamsFromURL(window.location.search);
      const execs = await getStats(type, params, dateRange, "orgExecustions");
      const { resources, ...userExecs } = processExecutions(execs);
      const fullRes = { userExecs, resources };
      return fullRes;
    } catch (error) {
      dispatch(errorMessage(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const requestOrganizationsStatsStatus = {
  [requestOrganizationsStats.fulfilled]: (state, action) => {
    state.statistics.resources = action.payload.resources;
    state.statistics.userExecs = action.payload.userExecs;
    state.executionLoading = false;
  },
  [requestOrganizationsStats.pending]: (state, action) => {
    state.executionLoading = true;
  },
  [requestOrganizationsStats.rejected]: (state, action) => {
    state.error = action.payload.error;
    state.executionLoading = false;
  },
};

/******** Get Organization dataset stats  **********/

export const requestOrganizationsStatsDataset = createAsyncThunk(
  `${slices.organizations}/requestOrganizationsStatsDataset`,
  async ({ type = "All", dateRange = DEFAULT_DATE_RANGE }, { rejectWithValue, dispatch }) => {
    try {
      const params = getParamsFromURL(window.location.search);
      const stats = await getStats(type, params, dateRange, "orgStatistics");
      const newData = stats.reduce((result, value, key) => {
        result[value.ID] = value;
        return result;
      }, {});
      return { dataStats: newData };
    } catch (error) {
      dispatch(errorMessage(error.message));
      return rejectWithValue(error.message);
    }
  }
);

export const requestOrganizationsStatsDatasetStatus = {
  [requestOrganizationsStatsDataset.fulfilled]: (state, action) => {
    state.statistics.dataStats = action.payload.dataStats;
    state.datasetLoading = false;
  },
  [requestOrganizationsStatsDataset.pending]: (state, action) => {
    state.datasetLoading = true;
  },
  [requestOrganizationsStatsDataset.rejected]: (state, action) => {
    state.error = action.payload.error;
    state.datasetLoading = false;
  },
};
